import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import TableHeader from 'components/UI/Table/TableHeader'
import TableBody from 'components/UI/Table/TableBody'
import TableButton from 'components/UI/Table/TableButton'
import Pagination from 'components/UI/Pagination'
import DoubleScrollbar from 'components/UI/DoubleScrollbar'
import { defaultTableValues, setPage, setFilter, triggerReload } from 'store/actions/tables'
import useFiltersChange from '../Filter/hooks/useFiltersChange'

function Table({ name, rowActions, small, hideSearchLink }) {
  const dispatch = useDispatch()
  const { filtersHaveChanged, triggerSearch } = useFiltersChange(name)

  const filterParsedKey = `search-content-parsed-${name}`
  const { [filterParsedKey]: filter, isLargeCompany } = useSelector(state => state.settings)
  let { [name]: tableOptions } = useSelector(state => state.tables)

  const showFiltersChangedMessage = filtersHaveChanged && isLargeCompany && !hideSearchLink

  // This line fixes a class of issues where the table is initted by a setCount or
  // something that does not properly init the table.
  if (!tableOptions?.initted) tableOptions = defaultTableValues

  let { [name]: tableRefreshCounter } = useSelector(state => state.refresh)
  const { archived, count, limit, page, sortBy, asyncContent } = tableOptions
  const showPagination = ((count > limit && limit !== Infinity) || page !== 1) && !showFiltersChangedMessage

  // Sets page back to first page when filter changes
  useEffect(() => {
    dispatch(setFilter(name, filter))
    dispatch(setPage(name, 1))
  }, [name, dispatch, filter])

  // Trigger the reload of the table content when an option has changed
  useEffect(() => {
    if (asyncContent) triggerReload(name)
  }, [archived, filter, limit, page, sortBy, dispatch, name, asyncContent, tableRefreshCounter])

  return (
    <>
      <DoubleScrollbar>
        <table className={`table ${small ? 'table-sm' : ''}`}>
          {/* Table header, we user the provided header to render the row */}
          <TableHeader name={name} filter={filter} rowActions={rowActions} options={tableOptions} />
          {/*  Table body, we use the provided body to render the content */}
          <TableBody
            name={name}
            options={tableOptions}
            rowActions={rowActions}
            showFiltersChangedMessage={showFiltersChangedMessage}
            triggerSearch={triggerSearch}
          />
        </table>
      </DoubleScrollbar>
      {/* Pagination */}
      {showPagination ? (
        <div className="pl-3 pt-3 pagination-container">
          <Pagination currentPage={page} pageLength={limit} numberOfItems={count} onPageChange={newPage => dispatch(setPage(name, newPage))} />
        </div>
      ) : null}
    </>
  )
}

export default Table

const headerProps = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string,
  type: PropTypes.string
})

Table.propTypes = {
  small: PropTypes.bool,
  header: PropTypes.arrayOf(headerProps),
  name: PropTypes.string.isRequired,
  rowActions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.func])) // used to pass the options of every row
}

export { TableBody, TableButton, TableHeader }
